import React, { useEffect, useState, useRef } from 'react';
import { StickyNoteIcon, SaveIcon, CheckIcon, AtSignIcon, SearchIcon, XIcon, BellIcon } from 'lucide-react';
import { Activity } from '../data/mockData';
interface NotesSectionProps {
  notes: Activity[];
  onAddNote: (note: Activity) => void;
}
interface User {
  id: string;
  name: string;
  initials: string;
  color: string;
}
const MOCK_USERS: User[] = [{
  id: 'u1',
  name: 'Alex Rivera',
  initials: 'AR',
  color: 'bg-orange-100 text-orange-700'
}, {
  id: 'u2',
  name: 'System',
  initials: 'SYS',
  color: 'bg-gray-200 text-gray-700'
}, {
  id: 'u3',
  name: 'Sarah Chen',
  initials: 'SC',
  color: 'bg-pink-100 text-pink-700'
}, {
  id: 'u4',
  name: 'David Park',
  initials: 'DP',
  color: 'bg-blue-100 text-blue-700'
}, {
  id: 'u5',
  name: 'Jennifer Torres',
  initials: 'JT',
  color: 'bg-amber-100 text-amber-700'
}, {
  id: 'u6',
  name: 'Michael Chang',
  initials: 'MC',
  color: 'bg-emerald-100 text-emerald-700'
}, {
  id: 'u7',
  name: 'Priya Patel',
  initials: 'PP',
  color: 'bg-purple-100 text-purple-700'
}, {
  id: 'u8',
  name: 'James Wilson',
  initials: 'JW',
  color: 'bg-red-100 text-red-700'
}];
export function NotesSection({
  notes,
  onAddNote
}: NotesSectionProps) {
  const combinedNotes = notes.map(n => n.details || n.summary).join('\n\n');
  const [noteText, setNoteText] = useState(combinedNotes);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [taggedUsers, setTaggedUsers] = useState<User[]>([]);
  const [notifySent, setNotifySent] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savedMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const notifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tagPopupRef = useRef<HTMLDivElement>(null);
  const tagButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!hasUnsavedChanges && !justSaved) {
      const combined = notes.map(n => n.details || n.summary).join('\n\n');
      setNoteText(combined);
    }
    if (justSaved) {
      setJustSaved(false);
    }
  }, [notes, hasUnsavedChanges, justSaved]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tagPopupRef.current && !tagPopupRef.current.contains(e.target as Node) && tagButtonRef.current && !tagButtonRef.current.contains(e.target as Node)) {
        setShowTagPopup(false);
        setTagSearch('');
      }
    }
    if (showTagPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagPopup]);
  const handleSaveNote = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (noteText.trim()) {
      const newNote: Activity = {
        id: `note-${Date.now()}`,
        type: 'note',
        timestamp: new Date().toISOString(),
        summary: noteText.trim(),
        details: noteText.trim(),
        author: 'You'
      };
      onAddNote(newNote);
    }
    setHasUnsavedChanges(false);
    setJustSaved(true);
    setShowSavedMessage(true);
    if (savedMessageTimeoutRef.current) {
      clearTimeout(savedMessageTimeoutRef.current);
    }
    savedMessageTimeoutRef.current = setTimeout(() => {
      setShowSavedMessage(false);
    }, 2000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(e.target.value);
    setHasUnsavedChanges(true);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      handleSaveNote();
    }, 2000);
  };
  const handleBlur = () => {
    if (hasUnsavedChanges) {
      handleSaveNote();
    }
  };
  const handleSelectUser = (user: User) => {
    // Don't add duplicates
    if (!taggedUsers.some(u => u.id === user.id)) {
      setTaggedUsers([...taggedUsers, user]);
    }
    setShowTagPopup(false);
    setTagSearch('');
  };
  const handleRemoveTaggedUser = (userId: string) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };
  const handleNotify = () => {
    // Simulate sending notification
    setNotifySent(true);
    if (notifyTimeoutRef.current) {
      clearTimeout(notifyTimeoutRef.current);
    }
    notifyTimeoutRef.current = setTimeout(() => {
      setNotifySent(false);
      setTaggedUsers([]);
    }, 2000);
  };
  const filteredUsers = MOCK_USERS.filter(u => u.name.toLowerCase().includes(tagSearch.toLowerCase()) && !taggedUsers.some(tagged => tagged.id === u.id));
  return <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 mb-6" data-id="element-127">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100" data-id="element-128">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-129">
          <StickyNoteIcon className="w-5 h-5 text-gray-600" data-id="element-130" />
          Notes
        </h3>
        {hasUnsavedChanges && <span className="text-xs text-amber-600 font-medium" data-id="element-131">
            Unsaved changes...
          </span>}
      </div>

      {/* Tagged Users Bar - shows above the textarea */}
      {taggedUsers.length > 0 && <div className="mb-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg" data-id="element-132">
          <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-133">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-134">
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-700 mr-1" data-id="element-135">
                Tagged:
              </span>
              {taggedUsers.map(user => <span key={user.id} className="inline-flex items-center gap-1.5 pl-1 pr-1 py-1 bg-white border border-blue-200 rounded-full text-sm text-gray-800 shadow-sm" data-id="element-136">
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold flex-shrink-0 ${user.color}`} data-id="element-137">
                    {user.initials}
                  </span>
                  <span className="font-medium pl-0.5" data-id="element-138">{user.name}</span>
                  <button type="button" onClick={() => handleRemoveTaggedUser(user.id)} className="ml-0.5 mr-0.5 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400" aria-label={`Remove ${user.name}`} data-id="element-139">
                    <XIcon className="w-3 h-3" data-id="element-140" />
                  </button>
                </span>)}
            </div>

            {/* Notify Button */}
            {notifySent ? <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-141">
                <CheckIcon className="w-4 h-4" data-id="element-142" />
                Notification sent
              </span> : <button type="button" onClick={handleNotify} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors shadow-sm whitespace-nowrap" data-id="element-143">
                <BellIcon className="w-4 h-4" data-id="element-144" />
                Notify Selected User{taggedUsers.length > 1 ? 's' : ''}
              </button>}
          </div>
        </div>}

      <div data-id="element-145">
        <textarea value={noteText} onChange={handleChange} onBlur={handleBlur} placeholder="Type your notes here..." className="w-full px-3 py-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent focus:bg-white placeholder-gray-400 resize-none transition-all max-h-[120px] min-h-[120px] overflow-y-auto" data-id="element-146" />

        {/* Action Row */}
        <div className="mt-3 flex items-center justify-between gap-3" data-id="element-147">
          <div className="relative" data-id="element-148">
            <button ref={tagButtonRef} type="button" onClick={() => setShowTagPopup(v => !v)} className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${showTagPopup ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} aria-label="Tag user" aria-expanded={showTagPopup} data-id="element-149">
              <AtSignIcon className="w-4 h-4" data-id="element-150" />
              Tag
            </button>

            {showTagPopup && <div ref={tagPopupRef} className="absolute left-full top-0 ml-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden" data-id="element-151">
                <div className="p-3 border-b border-gray-100" data-id="element-152">
                  <div className="relative" data-id="element-153">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-154" />
                    <input ref={searchInputRef} type="text" value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search users..." className="w-full pl-9 pr-3 py-2 text-sm text-gray-900 bg-white border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400" data-id="element-155" />
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto py-1" data-id="element-156">
                  {filteredUsers.length > 0 ? filteredUsers.map(user => <button key={user.id} type="button" onClick={() => handleSelectUser(user)} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50" data-id="element-157">
                        <span className={`flex items-center justify-center w-8 h-8 rounded-md text-xs font-semibold flex-shrink-0 ${user.color}`} data-id="element-158">
                          {user.initials}
                        </span>
                        <span className="font-medium" data-id="element-159">{user.name}</span>
                      </button>) : <div className="px-3 py-6 text-center text-sm text-gray-400" data-id="element-160">
                      {tagSearch ? 'No users found' : 'All users already tagged'}
                    </div>}
                </div>
              </div>}
          </div>

          <div className="flex items-center gap-3" data-id="element-161">
            {showSavedMessage && !hasUnsavedChanges && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-id="element-162">
                <CheckIcon className="w-4 h-4" data-id="element-163" />
                Saved
              </span>}
            {hasUnsavedChanges && <button onClick={handleSaveNote} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm" data-id="element-164">
                <SaveIcon className="w-4 h-4" data-id="element-165" />
                Save Notes
              </button>}
          </div>
        </div>
      </div>
    </div>;
}